export type ChipRowLayout = {
  visibleCount: number;
  hiddenCount: number;
};

/**
 * How many leading chips fit in `availableWidth`, collapsing the rest into one
 * +N chip.
 *
 * Chips keep their order and collapse from the right. When any chip is hidden
 * the +N chip's own width (plus its gap) is reserved first, so a +N never
 * pushes a visible chip past the edge, and a partial chip is never counted as
 * visible. Widths are whatever the caller measured — this function knows
 * nothing about the DOM.
 */
export const fitChips = (
  chipWidths: number[],
  availableWidth: number,
  gap: number,
  overflowChipWidth: number,
): ChipRowLayout => {
  const total = chipWidths.length;

  const rowWidth = (count: number) =>
    chipWidths
      .slice(0, count)
      .reduce((sum, width, index) => sum + width + (index > 0 ? gap : 0), 0);

  if (rowWidth(total) <= availableWidth) {
    return { visibleCount: total, hiddenCount: 0 };
  }

  for (let count = total - 1; count >= 0; count -= 1) {
    const withOverflow =
      rowWidth(count) + (count > 0 ? gap : 0) + overflowChipWidth;

    if (withOverflow <= availableWidth) {
      return { visibleCount: count, hiddenCount: total - count };
    }
  }

  return { visibleCount: 0, hiddenCount: total };
};
